import { Component, HostBinding, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams } from 'ag-grid-community';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Overlay } from '@angular/cdk/overlay';
export interface CustomColumnAddRequest {
  name: string;
  dataType: string;
  isRequired: boolean;
  isVisibleInGrid: boolean;
  isVisibleInForm: boolean;
}

export interface CustomColumnHeaderParams extends IHeaderParams {
  mode: 'add' | 'rename';
  columnName?: string;
  columnId?: number;
  canAdd?: boolean;
  canRename?: boolean;
  onAdd?: (
    name: string,
    dataType: string,
    options?: Partial<CustomColumnAddRequest>,
  ) => void;
  onRename?: (columnId: number, name: string) => void;
  onDelete?: (columnId: number) => void;
}

@Component({
  selector: 'app-custom-column-header',
  standalone: true,
  templateUrl: './custom-column-header.component.html',
  styleUrls: ['./custom-column-header.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDialogModule,
    MatInputModule,
    MatSlideToggleModule,
    MatInputModule,
  ],
})
export class CustomColumnHeaderComponent implements IHeaderAngularComp {
  @ViewChild('renameTrigger') renameMenuTrigger!: MatMenuTrigger;
  @ViewChild('addColumnDialog') addColumnDialog!: TemplateRef<any>;

  @HostBinding('class.rename-mode') get isRenameMode() {
    return this.mode === 'rename';
  }

  params!: CustomColumnHeaderParams;

  mode: 'add' | 'rename' = 'add';
  columnName = '';
  columnId = 0;

  canAdd = false;
  canRename = false;
  canDelete = false;

  inputControl = new FormControl('');
  dataTypeControl = new FormControl('text');
  isRequiredControl = new FormControl(false);
  isVisibleInGridControl = new FormControl(true);
  isVisibleInFormControl = new FormControl(true);

  constructor(
    private dialog: MatDialog,
    private overlay: Overlay,
  ) {}

  agInit(params: CustomColumnHeaderParams): void {
    this.params = params;
    this.mode = params.mode;
    this.columnName = params.columnName || '';
    this.columnId = params.columnId || 0;
    this.canAdd = params.canAdd ?? false;
    this.canRename = params.canRename ?? false;
    this.canDelete = !!params.onDelete;

    this.inputControl.setValue(this.columnName);
    this.resetAddForm();
  }

  refresh(params: CustomColumnHeaderParams): boolean {
    this.agInit(params);
    return true;
  }

  onMenuOpen(): void {
    this.inputControl.setValue(this.mode === 'rename' ? this.columnName : '');
  }

  openAddColumnDialog(): void {
    this.resetAddForm();

    this.dialog.open(this.addColumnDialog, {
      width: '520px',
      maxWidth: '94vw',
      panelClass: 'custom-column-dialog-panel',
      autoFocus: false,
      restoreFocus: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
  }

  private resetAddForm(): void {
    this.inputControl.setValue('');
    this.dataTypeControl.setValue('text');
    this.isRequiredControl.setValue(false);
    this.isVisibleInGridControl.setValue(true);
    this.isVisibleInFormControl.setValue(true);
  }

  confirmAdd(): void {
    const name = this.inputControl.value?.trim() ?? '';
    const dataType = this.dataTypeControl.value ?? 'text';

    if (!name) return;

    this.params.onAdd?.(name, dataType, {
      name,
      dataType,
      isRequired: false,
      isVisibleInGrid: !!this.isVisibleInGridControl.value,
      isVisibleInForm: !!this.isVisibleInFormControl.value,
    });

    this.resetAddForm();
    this.dialog.closeAll();
  }

  confirmRename(): void {
    const name = this.inputControl.value?.trim() ?? '';

    if (!name || name === this.columnName) return;

    this.params.onRename?.(this.columnId, name);
    this.renameMenuTrigger?.closeMenu();
  }

  confirmDelete(): void {
    this.params.onDelete?.(this.columnId);
    this.renameMenuTrigger?.closeMenu();
  }
}
